import { definePreset, palette } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';
import type { Branding } from '@erebus/runtime-config';
import { brands } from './brands';

/**
 * Light-mode component color overrides for Message/Toast, preserving the
 * frontend's established status styling. These status colors are brand-independent.
 */
const messageColors = {
  error: { background: '#FBE7EC', border: '#CC5B67', text: '#C00C1C', hoverBackground: '#FFB4BC' },
  success: {
    background: '#E0FDE6',
    border: '#2F8D44',
    text: '#125410',
    hoverBackground: '#9BEFB0',
  },
  info: { background: '#E0FCFF', border: '#5B66A8', text: '#444F98', hoverBackground: '#B4E2FF' },
  warn: { background: '#FFF4DE', border: '#B5735A', text: '#9B4300', hoverBackground: '#F8CE76' },
} as const;

const toastColors = {
  error: { background: '#FFD3D8', border: '#C00C1C', text: '#C00C1C', hoverBackground: '#FFB4BC' },
  success: {
    background: '#BFFFCD',
    border: '#2F8D44',
    text: '#125410',
    hoverBackground: '#9BEFB0',
  },
  info: { background: '#C3F6FF', border: '#0058A0', text: '#0058A0', hoverBackground: '#B4E2FF' },
  warn: { background: '#FAE9C3', border: '#B5735A', text: '#9B4300', hoverBackground: '#F8CE76' },
} as const;

interface SeverityColor {
  background: string;
  border: string;
  text: string;
  hoverBackground: string;
}
type Severity = 'error' | 'success' | 'info' | 'warn';
type SeverityColors = Record<Severity, SeverityColor>;

function severityScheme(c: SeverityColors) {
  const map = (s: SeverityColor) => ({
    background: s.background,
    borderColor: s.border,
    color: s.text,
    closeButton: { hoverBackground: s.hoverBackground },
  });
  return {
    error: map(c.error),
    success: map(c.success),
    info: map(c.info),
    warn: map(c.warn),
  };
}

/**
 * Build the PrimeVue preset for a given brand. The preset is the single source
 * of truth for design tokens; tailwindcss-primeui exposes these same tokens as
 * Tailwind utilities in each consuming app.
 *
 * @param branding - runtime-config branding discriminator; unknown values fall back to praxes.
 */
export function createPreset(branding: Branding) {
  const brand = brands[branding] ?? brands.praxes;

  const primary = palette(brand.primary);
  const surface = palette(brand.surface);

  return definePreset(Aura, {
    primitive: {
      // Remap the primitives Aura's components reference, so brand and status
      // colors flow through. Aura uses two hue vocabularies for info/warn:
      // sky/orange (Badge, Button, Tag) and blue/yellow (Message, Toast,
      // InlineMessage, ProgressSpinner) — remap all four to the status seeds.
      // zinc is deliberately NOT remapped: it backs Aura's dark surface ramp,
      // which stays stock (tuned for dark contrast).
      green: palette(brand.success),
      red: palette(brand.danger),
      sky: palette(brand.info),
      blue: palette(brand.info),
      orange: palette(brand.warning),
      yellow: palette(brand.warning),
    },
    semantic: {
      primary,
      // Brand the light surface ramp only. Dark mode otherwise keeps Aura's
      // stock dark scheme: a zinc surface ramp tuned for dark contrast, and
      // primary shades picked from the shared brand ramp ({primary.400} et al).
      colorScheme: {
        light: { surface },
        dark: {
          formField: {
            // Aura reaches for {red.300} here, the lightest step, to maximise
            // contrast against a dark field. It succeeds at that and fails at
            // the job: on our danger ramp that step is desaturated enough
            // (0.40) to read as a pale line rather than an error, so on a
            // near-black field it looks like a stray white border.
            //
            // {red.400} clears the 3:1 minimum for a UI component with room to
            // spare (5.6:1, above light mode's own 3.5:1) and is the step light
            // mode already uses, so the error reads the same in both schemes.
            invalidBorderColor: '{red.400}',
          },
        },
      },
    },
    components: {
      // Light-only on purpose: definePreset deep-merges, so dark mode keeps
      // Aura's stock dark severity schemes, which derive from the
      // brand-remapped status primitives above.
      message: { colorScheme: { light: severityScheme(messageColors) } },
      toast: { colorScheme: { light: severityScheme(toastColors) } },
    },
  });
}
